import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { formatDateTime } from "@/lib/format";

type Customer = components["schemas"]["CustomerResponse"];

function formatAddress(c: Customer): string | null {
  const parts = [c.addressLine1, c.addressLine2, c.city, c.state, c.postalCode, c.country].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length ? parts.join(", ") : null;
}

export function CustomerPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get("organisationId") ?? undefined;

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId || !organisationId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function fetchCustomer() {
      setLoading(true);
      const { data, error: apiError } = await api.GET("/v1/customers/{id}", {
        params: { path: { id: customerId! } },
        headers: { "x-dolfin-organisation-id": organisationId! },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load customer.");
      } else {
        setCustomer(data ?? null);
      }
      setLoading(false);
    }

    fetchCustomer();
    return () => {
      cancelled = true;
    };
  }, [customerId, organisationId]);

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[100rem] p-8">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Customer</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      ) : customer ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {customer.name || <span className="text-muted-foreground">Unnamed</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Section title="Customer">
              <Field label="Customer ID">
                <Badge variant="secondary" className="font-mono text-xs">
                  {customer.id}
                </Badge>
              </Field>
              <Field label="Organisation">
                {customer.organisationId ? (
                  <Link to={`/organisations/${customer.organisationId}`}>
                    <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                      {customer.organisationId}
                    </Badge>
                  </Link>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>
              <Field label="Country">{customer.country ?? "—"}</Field>
            </Section>

            <Section title="Contact">
              <Field label="Email">{customer.email ?? "—"}</Field>
              <Field label="Phone">{customer.phone ?? "—"}</Field>
              <Field label="Tax number">{customer.taxNumber ?? "—"}</Field>
              <Field label="Address" className="sm:col-span-3">
                {formatAddress(customer) ?? "—"}
              </Field>
            </Section>

            <Section title="Audit">
              <Field label="Created">{formatDateTime(customer.createdAt)}</Field>
              <Field label="Created by">
                <ActorLink actorId={customer.createdBy} />
              </Field>
              <Field label="Updated">{formatDateTime(customer.updatedAt)}</Field>
              <Field label="Updated by">
                <ActorLink actorId={customer.updatedBy} />
              </Field>
            </Section>
          </CardContent>
        </Card>
      ) : !organisationId ? (
        <p className="text-muted-foreground">
          Missing organisationId query parameter — customer endpoint requires an organisation context.
        </p>
      ) : (
        <p className="text-muted-foreground">Customer not found.</p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid [&>div]:min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">{children}</dl>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

function ActorLink({ actorId }: { actorId: string | null | undefined }) {
  if (!actorId) return <span className="text-muted-foreground">—</span>;
  return (
    <Link to={`/users/${actorId}`}>
      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
        {actorId.slice(0, 8)}...
      </Badge>
    </Link>
  );
}
